import React, { useMemo } from 'react';
import { Box, Typography, IconButton, Tooltip } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { mobileNavIconMap } from '../../../config/mobileAppNavigation';
import { getPermissionDeniedMessage } from '../../../auth/permissions';
import {
  mobileAppBackground,
  mobileAppGlassCard,
  mobileHideScrollbarSx,
  mobileNavGridItemLabelSx,
  mobileQuickActionSx,
  mobileSectionTitleSx,
} from './mobileAppTheme';

const GROUP_COLORS = {
  overview: ['#4ce1b6', '#60a5fa'],
  bookings: ['#4ce1b6', '#60a5fa', '#a78bfa', '#38bdf8', '#f97316', '#818cf8', '#94a3b8'],
  insights: ['#818cf8', '#60a5fa', '#4ce1b6', '#a78bfa', '#38bdf8', '#f97316'],
  apps: ['#60a5fa', '#4ce1b6', '#f97316', '#a78bfa', '#94a3b8', '#818cf8'],
  operations: ['#a78bfa', '#4ce1b6', '#60a5fa'],
};

export default function MobileMoreDrawer({ open, onClose, groups, onNavigate }) {
  const menuGroups = useMemo(() => {
    const order = [
      ['overview', 'Overview'],
      ['bookings', 'Booking'],
      ['insights', 'Insights'],
      ['apps', 'Apps'],
      ['operations', 'Operations'],
    ];
    return order.map(([groupKey, title]) => {
      const colors = GROUP_COLORS[groupKey] || GROUP_COLORS.overview;
      return {
        groupKey,
        title,
        items: (groups?.[groupKey] || []).map((item, index) => ({
          ...item,
          color: colors[index % colors.length],
          menuKey: `${groupKey}-${item.url}`,
        })),
      };
    }).filter((group) => group.items.length > 0);
  }, [groups]);

  if (!open) return null;

  return (
    <Box
      role="presentation"
      sx={{
        position: 'fixed',
        inset: 0,
        // Below DRAWER_MENU (1800) would hide selects; stay under MAXIMUM overlays.
        zIndex: 1750,
        pointerEvents: 'auto',
      }}
    >
      <Box
        aria-hidden
        onClick={onClose}
        sx={{
          ...mobileAppBackground,
          position: 'absolute',
          inset: 0,
        }}
      />
      <Box
        role="dialog"
        aria-modal="true"
        aria-label="All menus"
        sx={{
          ...mobileAppBackground,
          position: 'absolute',
          inset: 0,
          height: '100%',
          maxHeight: 'none',
          overflow: 'hidden',
          borderRadius: 0,
          pb: 'calc(76px + env(safe-area-inset-bottom))',
        }}
      >
        <Box sx={{ px: 1.5, pt: 'calc(14px + env(safe-area-inset-top))' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.25 }}>
            <Typography sx={{ ...mobileSectionTitleSx, fontSize: '1.05rem' }}>All menus</Typography>
            <IconButton onClick={onClose} aria-label="Close menu" sx={{ color: '#94a3b8' }}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Box
            sx={{
              maxHeight: 'calc(100vh - 120px - env(safe-area-inset-top) - env(safe-area-inset-bottom))',
              overflowY: 'auto',
              pr: 0,
              ...mobileHideScrollbarSx,
            }}
          >
            {menuGroups.map((group) => (
              <Box key={group.groupKey} sx={{ mb: 1.6 }}>
                <Typography
                  sx={{
                    mb: 0.8,
                    color: 'rgba(226, 232, 240, 0.92)',
                    fontSize: '0.76rem',
                    fontWeight: 800,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                  }}
                >
                  {group.title}
                </Typography>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                    gap: 1,
                  }}
                >
                  {group.items.map((item) => {
                    const Icon = mobileNavIconMap[item.icon] || mobileNavIconMap['lnr-home'];
                    const label = item.shortTitle || item.title;
                    const disabled = Boolean(item.disabled);
                    const deniedPermissionCode = item.permissionCode || item.permissionCodes?.[0];
                    const tile = (
                      <Box
                        sx={{
                          ...mobileQuickActionSx,
                          minWidth: 0,
                          minHeight: 78,
                          p: 1,
                          gap: 0.55,
                          ...mobileAppGlassCard,
                          background:
                            'linear-gradient(155deg, rgba(3,7,18,0.82) 0%, rgba(15,23,42,0.78) 100%)',
                          borderColor: 'rgba(76, 225, 182, 0.14)',
                          opacity: disabled ? 0.45 : 1,
                          cursor: disabled ? 'not-allowed' : 'pointer',
                          width: '100%',
                        }}
                        onClick={() => {
                          if (disabled) return;
                          onNavigate(item.url);
                          onClose();
                        }}
                      >
                        <Box
                          sx={{
                            width: 34,
                            height: 34,
                            borderRadius: '11px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: `${item.color}22`,
                            color: item.color,
                            flexShrink: 0,
                          }}
                        >
                          <Icon sx={{ fontSize: 18 }} />
                        </Box>
                        <Typography sx={mobileNavGridItemLabelSx}>{label}</Typography>
                      </Box>
                    );

                    if (!disabled) {
                      return <React.Fragment key={item.menuKey}>{tile}</React.Fragment>;
                    }

                    return (
                      <Tooltip
                        key={item.menuKey}
                        title={getPermissionDeniedMessage(deniedPermissionCode, label)}
                        arrow
                        placement="top"
                      >
                        <Box component="span" sx={{ display: 'block', minWidth: 0 }}>
                          {tile}
                        </Box>
                      </Tooltip>
                    );
                  })}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
